import type { DiskStage, QueueEntry } from './importWizardMachine';

export type PrimaryAction = {
    readonly labelId: string;
    readonly disabled: boolean;
    readonly busy: boolean;
    /** Tooltip shown while the button is disabled and idle; absent means none. */
    readonly hintId?: string;
    readonly kind: 'commit' | 'restart';
};

export type FooterActions = {
    readonly cancelLabelId: string;
    /** Absent while revisiting a resolved entry: Skip/Undo live in the review banner instead. */
    readonly primary?: PrimaryAction;
    readonly showSkip: boolean;
};

const CANCEL = 'patientList.import.action.cancel';

/**
 * The footer's enable/disable/hint matrix, as a function of the stage and the entry on screen.
 *
 * `offerPending` is true while a newly dropped folder is still being read (a collect walk in flight):
 * the review on screen is about to be replaced by that drop, so its commit/skip actions are held:
 * committing the old selection now would win the race and make the machine refuse the incoming drop.
 */
export function footerActions(
    stage: DiskStage,
    entry: QueueEntry | undefined,
    committing: boolean,
    offerPending: boolean
): FooterActions {
    if (stage.kind === 'done') {
        return {
            cancelLabelId: 'patientList.import.action.close',
            primary: {
                labelId: 'patientList.import.action.importAnother',
                disabled: false,
                busy: false,
                kind: 'restart',
            },
            showSkip: false,
        };
    }

    const reviewOpen =
        stage.kind === 'reviewing' || stage.kind === 'committing';
    if (reviewOpen && entry !== undefined) {
        if (entry.status.kind !== 'waiting') {
            return { cancelLabelId: CANCEL, showSkip: false };
        }
        const target =
            entry.verdict.kind === 'resolved'
                ? entry.verdict.target
                : undefined;
        const nothingSelected = entry.selectedSeries.size === 0;
        // The all-already-stored case needs its own hint; "select a dataset" would send the reviewer hunting.
        const allAlreadyStored =
            nothingSelected &&
            entry.alreadyStored !== undefined &&
            entry.alreadyStored.size > 0;
        return {
            cancelLabelId: CANCEL,
            primary: {
                labelId: target
                    ? target.kind === 'new-patient'
                        ? 'patientList.import.action.importAsNew'
                        : 'patientList.import.action.importToExisting'
                    : 'patientList.import.action.import',
                disabled:
                    committing ||
                    offerPending ||
                    entry.verdict.kind !== 'resolved' ||
                    nothingSelected,
                busy: committing,
                hintId: offerPending
                    ? 'patientList.import.hint.reading'
                    : entry.verdict.kind === 'checking'
                      ? 'patientList.import.hint.checking'
                      : entry.verdict.kind === 'failed'
                        ? 'patientList.import.hint.checkFailed'
                        : allAlreadyStored
                          ? 'patientList.import.hint.allAlreadyImported'
                          : nothingSelected
                            ? 'patientList.import.hint.selectDataset'
                            : undefined,
                kind: 'commit',
            },
            showSkip: !committing && !offerPending,
        };
    }

    return {
        cancelLabelId: CANCEL,
        primary: {
            labelId: 'patientList.import.action.import',
            disabled: true,
            busy: false,
            hintId:
                stage.kind === 'expanding'
                    ? 'patientList.import.hint.openingArchive'
                    : stage.kind === 'scanning'
                      ? 'patientList.import.hint.scanning'
                      : 'patientList.import.hint.dropFolder',
            kind: 'commit',
        },
        showSkip: false,
    };
}
